import type { ClassValue } from 'clsx';

import { shouldDisplayOriginalPrice } from '@vs-shop/domain/price/getDiscountPercentageFromPrices';
import { PriceTagCurrent, PriceTagOriginal } from '@vs-shop/ui/priceTag';

import {
  ProductCardPrice as ProductCardPricePart,
  ProductCardPriceOriginalValue,
  type ProductCardVariant,
} from '@vs-shop/commerce-ui/productCard';

import { clsxm } from '@vs-shop/utils';

import { getDefaultCurrency } from '../../../../config/currency';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import { IntlMoney } from '../../intl/Intl';

export type ProductCardPriceProps = {
  /** Final sell price in minor units — raw `number` until `ProductCardData` ships branded money (ledger debt). */
  price: number;
  /** Pre-discount price in minor units, or `null` when the product has no discount. */
  originalPrice: number | null;
  variant: ProductCardVariant;
  className?: ClassValue;
};

/**
 * rcprofi's money in the card's price stack.
 *
 * Which alignment and which struck-through size each appearance asks for is the
 * section's; the currency, the formatting and the rule for whether a
 * pre-discount price is worth showing at all are this tenant's.
 */
export function ProductCardPrice({
  price,
  originalPrice,
  variant,
  className,
}: ProductCardPriceProps) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const showOriginalPrice =
    originalPrice !== null &&
    originalPrice > price &&
    shouldDisplayOriginalPrice(price, originalPrice);
  const currency = getDefaultCurrency();

  return (
    <ProductCardPricePart variant={variant} className={clsxm(className)}>
      {/* ORIGINAL PRICE */}
      {/* TODO: LOYALTY only with ORIGINAL PRICE - this might not fit the current logic once it is working  */}
      <PriceTagOriginal>
        {showOriginalPrice && (
          <ProductCardPriceOriginalValue variant={variant}>
            <IntlMoney value={{ amount: originalPrice, currency: currency }} />
          </ProductCardPriceOriginalValue>
        )}
      </PriceTagOriginal>

      {/* CURRENT PRICE */}
      <PriceTagCurrent>
        {price <= 0 ? (
          intl.formatMessage({
            defaultMessage: 'Cena na dotaz',
            id: 'component.client.product.common.priceRequest',
          })
        ) : (
          <IntlMoney value={{ amount: price, currency: currency }} />
        )}
      </PriceTagCurrent>
    </ProductCardPricePart>
  );
}
